import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useErpCareerGrowthDictionaryQuery,
  useErpCareerSiteCoursesQuery,
  useSaveAchievementMutation,
  useSaveGrowthTrackMutation,
  useSaveSiteCourseMutation,
  useToggleAchievementMutation,
  useToggleGrowthTrackMutation,
  useToggleSiteCourseMutation,
} from 'core/graphql';

import { ISkillMutationResult } from './careerModel';

const result = (payload: any): ISkillMutationResult => ({
  success: Boolean(payload?.success),
  errors: (payload?.customErrors ?? []).map((item: any) => ({
    field: item?.field ?? '',
    message: item?.message ?? '',
  })),
});

const EMPTY: any[] = [];

/**
 * ТЗ 0040 фаза C — справочники роста и витрины.
 *
 * До этой фазы профессии, медали и курсы витрины правились ТОЛЬКО в
 * Django-админке и сидами: изменение прайс-листа означало выкат.
 */
/**
 * Профессии и медали приезжают ОДНИМ запросом `ErpCareerGrowthDictionary` —
 * так было заведено, когда обе таблицы жили на одной панели. Две страницы
 * разделять его не стали: Apollo отдаст второй из кеша, а два запроса вместо
 * одного ради разнесения по адресам — плата ни за что.
 */
export const useGrowthTracks = () => {
  const { t } = useTranslation();
  const { data, loading, refetch } = useErpCareerGrowthDictionaryQuery({
    fetchPolicy: 'cache-and-network',
  });

  // Словарь значков приезжает тем же запросом, что и сами профессии: это одна
  // форма, и второй запрос дал бы форму, открывающуюся раньше своего пикера.
  // У профессии официального логотипа нет, поэтому набор фигур курируем мы —
  // и раздаёт его сервер, чтобы пикер не предложил значение, которое
  // `upsertTrack` отвергнет.
  //
  // 🔴 Подписи перелокализуются ЗДЕСЬ — тем же приёмом, что у реестра правил
  // (`useGrowthRules` в `usePromoCodes.ts`). Сервер отдаёт их по-русски:
  // строки помечены `gettext`, но польского каталога для них нет, а ERP у
  // владельца работает на польском. Без этой обёртки в польской форме
  // профессии висело «Щит — безопасность». Серверная подпись остаётся
  // запасной: новый значок без ключа покажется по-русски, а не путём ключа.
  const trackIcons = useMemo(
    () =>
      (data?.careerTrackIcons ?? []).map((row) => ({
        ...row,
        label: t(`pages.careerDict.professions.iconLabel.${row.value}`, {
          defaultValue: row.label,
        }),
      })),
    [data, t],
  );

  return {
    tracks: data?.careerGrowthTracks ?? EMPTY,
    trackIcons,
    loading,
    refetch,
  };
};

export const useAchievements = () => {
  const { data, loading, refetch } = useErpCareerGrowthDictionaryQuery({
    fetchPolicy: 'cache-and-network',
  });
  return {
    achievements: data?.careerAchievements ?? EMPTY,
    loading,
    refetch,
  };
};

export const useSiteCourses = (includeHidden = false) => {
  const { data, loading, refetch } = useErpCareerSiteCoursesQuery({
    variables: { includeHidden },
    fetchPolicy: 'cache-and-network',
  });
  return {
    siteCourses: data?.careerSiteCourses ?? EMPTY,
    loading,
    refetch,
  };
};

export const useGrowthDictionaryMutations = (onDone?: () => void) => {
  const [saveTrack, saveTrackState] = useSaveGrowthTrackMutation();
  const [toggleTrack, toggleTrackState] = useToggleGrowthTrackMutation();
  const [saveMedal, saveMedalState] = useSaveAchievementMutation();
  const [toggleMedal, toggleMedalState] = useToggleAchievementMutation();
  const [saveCourse, saveCourseState] = useSaveSiteCourseMutation();
  const [toggleCourse, toggleCourseState] = useToggleSiteCourseMutation();

  const done = useCallback(
    (payload: any) => {
      const out = result(payload);
      if (out.success && onDone) onDone();
      return out;
    },
    [onDone],
  );

  return {
    pending:
      saveTrackState.loading ||
      toggleTrackState.loading ||
      saveMedalState.loading ||
      toggleMedalState.loading ||
      saveCourseState.loading ||
      toggleCourseState.loading,

    saveTrack: useCallback(
      async (input: any) => {
        const { data } = await saveTrack({ variables: { input } });
        return done(data?.saveGrowthTrack);
      },
      [saveTrack, done],
    ),
    setTrackActive: useCallback(
      async (trackId: string, isActive: boolean) => {
        const { data } = await toggleTrack({
          variables: { trackId, isActive },
        });
        return done(data?.toggleGrowthTrack);
      },
      [toggleTrack, done],
    ),
    saveAchievement: useCallback(
      async (input: any) => {
        const { data } = await saveMedal({ variables: { input } });
        return done(data?.saveAchievement);
      },
      [saveMedal, done],
    ),
    setAchievementActive: useCallback(
      async (achievementId: string, isActive: boolean) => {
        const { data } = await toggleMedal({
          variables: { achievementId, isActive },
        });
        return done(data?.toggleAchievement);
      },
      [toggleMedal, done],
    ),
    saveSiteCourse: useCallback(
      async (input: any) => {
        const { data } = await saveCourse({ variables: { input } });
        return done(data?.saveSiteCourse);
      },
      [saveCourse, done],
    ),
    setSiteCourseActive: useCallback(
      async (siteCourseId: string, isActive: boolean) => {
        const { data } = await toggleCourse({
          variables: { siteCourseId, isActive },
        });
        return done(data?.toggleSiteCourse);
      },
      [toggleCourse, done],
    ),
  };
};
